import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TeamProvider, useTeam } from './context/TeamContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navigation from './components/Navigation';
import DemoBanner from './components/DemoBanner';
import PageViewTracker from './components/PageViewTracker';
import BlurredUnavailable from './components/BlurredUnavailable';
import TeamSelectModal from './components/TeamSelectModal';
import LoadingSpinner from './components/LoadingSpinner';
import Login from './pages/Login';

// Pages load on first visit (one small bundle each) so the first screen comes up fast.
const Home = lazy(() => import('./pages/Home'));
const LineupReport = lazy(() => import('./pages/LineupReport'));
const HitterReports = lazy(() => import('./pages/HitterReports'));
const CatcherFramingReport = lazy(() => import('./pages/CatcherFramingReport'));
const OpposingPitchingReport = lazy(() => import('./pages/OpposingPitchingReport'));
const InfieldReport = lazy(() => import('./pages/InfieldReport'));
const PostGamePitching = lazy(() => import('./pages/PostGamePitching'));
const PitcherUpload = lazy(() => import('./pages/PitcherUpload'));
const BatterUpload = lazy(() => import('./pages/BatterUpload'));
const AtBatReports = lazy(() => import('./pages/AtBatReports'));
const PitcherVideo = lazy(() => import('./pages/PitcherVideo'));
const JucoLeaderboard = lazy(() => import('./pages/JucoLeaderboard'));
const Portal = lazy(() => import('./pages/Portal'));
const HighSchool = lazy(() => import('./pages/HighSchool'));
const CollegeLeaderboard = lazy(() => import('./pages/CollegeLeaderboard'));
const DugoutPostGame = lazy(() => import('./pages/DugoutPostGame'));
const DugoutScouting = lazy(() => import('./pages/DugoutScouting'));
const SouthAlabamaReports = lazy(() => import('./pages/SouthAlabamaReports'));
const CreateYourOwnTable = lazy(() => import('./pages/CreateYourOwnTable'));
const PlayerHitterPage = lazy(() => import('./pages/PlayerHitterPage'));
const PlayerPitcherPage = lazy(() => import('./pages/PlayerPitcherPage'));
const HighSchoolPlayerPage = lazy(() => import('./pages/HighSchoolPlayerPage'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const FillTMIds = lazy(() => import('./pages/FillTMIds'));
const ManualPipeline = lazy(() => import('./pages/ManualPipeline'));
const PitchRetagging = lazy(() => import('./pages/PitchRetagging'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UserActivity = lazy(() => import('./pages/UserActivity'));

// Every signed-in page. Admin-only pages (user management, Fill TM IDs, Manual Pipeline, Admin
// Dashboard, User Activity, Leaderboard, High School) check the role inside the page; the Pitch
// Retagger checks Admin / South Alabama in the page and on every /api/admin/retag route.
const PAGES = [
  ['/', Home],
  ['/lineup-card', LineupReport],
  ['/post-game-hitter-reports', HitterReports],
  ['/catcher-framing-reports', CatcherFramingReport],
  ['/opposing-pitching-report', OpposingPitchingReport],
  ['/infield-outfield-report', InfieldReport],
  ['/post-game-pitching-reports', PostGamePitching],
  ['/pitcher-upload', PitcherUpload],
  ['/batter-upload', BatterUpload],
  ['/swing-decision-reports', AtBatReports],
  ['/pitcher-video', PitcherVideo],
  ['/high-school', HighSchool],
  ['/leaderboard', CollegeLeaderboard],
  ['/dugout/post-game', DugoutPostGame],
  ['/dugout/scouting', DugoutScouting],
  ['/dugout/south-alabama', SouthAlabamaReports],
  ['/dugout/custom-table', CreateYourOwnTable],
  ['/player/hitter/:playerId', PlayerHitterPage],
  ['/player/pitcher/:playerId', PlayerPitcherPage],
  ['/player/high-school-pitcher/:pitcherIds', HighSchoolPlayerPage],
  ['/user-management', UserManagement],
  ['/fill-tm-ids', FillTMIds],
  ['/manual-pipeline', ManualPipeline],
  ['/pitch-retagging', PitchRetagging],
  ['/admin-dashboard', AdminDashboard],
  ['/user-activity', UserActivity],
];

// Recruiting boards that are blurred for accounts without TrackMan access to other teams.
const GATED_PAGES = [
  ['/juco-leaderboard', JucoLeaderboard],
  ['/portal', Portal],
];

// Until a team is chosen, every page sits behind the team picker.
function TeamPicker() {
  const { selectedTeam } = useTeam();
  return selectedTeam ? null : <TeamSelectModal />;
}

function SignedIn() {
  return (
    <ProtectedRoute>
      <PageViewTracker />
      <Navigation />
      <DemoBanner />
      <TeamPicker />
      <Suspense fallback={<div className="app-boot"><LoadingSpinner /></div>}>
        <Routes>
          {PAGES.map(([path, Page]) => <Route key={path} path={path} element={<Page />} />)}
          {GATED_PAGES.map(([path, Page]) => (
            <Route key={path} path={path} element={<BlurredUnavailable><Page /></BlurredUnavailable>} />
          ))}
        </Routes>
      </Suspense>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TeamProvider>
        <BrowserRouter>
          <div className="app-shell">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/*" element={<SignedIn />} />
            </Routes>
          </div>
        </BrowserRouter>
      </TeamProvider>
    </AuthProvider>
  );
}
